import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import {
    formatDateAndTimeWithWeekday,
    formatTime,
} from '@apps/web/lib/date_utils';
import {
    ApiError,
    type JoinedOutreachRead,
} from '@libraries/typescript/api_client';

type ManualContactConflictAssignment = {
    readonly requestedShiftId: string;
    readonly conflictingShiftId: string;
    readonly patientName: string;
    readonly shiftStartTime: string;
    readonly shiftEndTime: string;
};

export type ManualContactConflictOverride = {
    readonly caregiverId: string;
    readonly shiftIds: ReadonlyArray<string>;
    readonly assignments: ReadonlyArray<ManualContactConflictAssignment>;
};

export function parseManualContactConflict(
    error: unknown,
    caregiverId: string,
): ManualContactConflictOverride | null {
    if (!(error instanceof ApiError) || error.status !== 409) {
        return null;
    }
    const detail = (error.body as { readonly detail?: unknown } | undefined)
        ?.detail;
    if (typeof detail !== 'object' || detail === null) {
        return null;
    }
    const detailRecord = detail as Readonly<Record<string, unknown>>;
    const conflicts = detailRecord['conflicts'];
    if (typeof conflicts !== 'object' || conflicts === null) {
        return null;
    }
    const shiftIds = Object.entries(
        conflicts as Readonly<Record<string, unknown>>,
    )
        .filter(
            ([, caregiverIds]) =>
                Array.isArray(caregiverIds) &&
                caregiverIds.includes(caregiverId),
        )
        .map(([shiftId]) => shiftId);
    if (shiftIds.length === 0) {
        return null;
    }

    const rawAssignments = detailRecord['conflicting_assignments'];
    const assignments = Array.isArray(rawAssignments)
        ? rawAssignments.flatMap((rawAssignment) => {
              if (typeof rawAssignment !== 'object' || rawAssignment === null) {
                  return [];
              }
              const assignment = rawAssignment as Readonly<
                  Record<string, unknown>
              >;
              const requestedShiftId = assignment['requested_shift_id'];
              const conflictingShiftId = assignment['conflicting_shift_id'];
              const patientName = assignment['patient_name'];
              const shiftStartTime = assignment['shift_start_time'];
              const shiftEndTime = assignment['shift_end_time'];
              if (
                  typeof requestedShiftId !== 'string' ||
                  typeof conflictingShiftId !== 'string' ||
                  typeof patientName !== 'string' ||
                  typeof shiftStartTime !== 'string' ||
                  typeof shiftEndTime !== 'string'
              ) {
                  return [];
              }
              return [
                  {
                      requestedShiftId,
                      conflictingShiftId,
                      patientName,
                      shiftStartTime,
                      shiftEndTime,
                  },
              ];
          })
        : [];
    return { caregiverId, shiftIds, assignments };
}

type ManualContactConflictDialogProps = {
    readonly conflict: ManualContactConflictOverride | null;
    readonly shifts: JoinedOutreachRead['shifts'];
    readonly timezone: string | undefined;
    readonly isCalling: boolean;
    readonly onCancel: () => void;
    readonly onConfirm: (
        caregiverId: string,
        shiftIds: ReadonlyArray<string>,
    ) => void;
};

export function ManualContactConflictDialog({
    conflict,
    shifts,
    timezone,
    isCalling,
    onCancel,
    onConfirm,
}: ManualContactConflictDialogProps) {
    const { formatName } = useFormatPersonName();
    const displayTimezone = timezone ?? 'UTC';
    return (
        <Dialog
            open={conflict !== null}
            onOpenChange={(open) => {
                if (!open) {
                    onCancel();
                }
            }}
        >
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Call despite schedule conflict?</DialogTitle>
                    <DialogDescription>
                        This caregiver has an overlapping assigned shift. Review
                        the exact assignment before calling again.
                    </DialogDescription>
                </DialogHeader>
                {conflict && (
                    <div className="space-y-3 text-sm">
                        <div>
                            <div className="font-medium">Outreach shifts</div>
                            <ul className="mt-1 list-disc pl-5">
                                {shifts
                                    .filter((shift) =>
                                        conflict.shiftIds.includes(shift.id),
                                    )
                                    .map((shift) => (
                                        <li key={shift.id}>
                                            {formatName(shift.patient_name)} (
                                            {formatDateAndTimeWithWeekday(
                                                shift.shift_start_time,
                                                displayTimezone,
                                            )}
                                            –
                                            {formatTime(
                                                shift.shift_end_time,
                                                displayTimezone,
                                            )}
                                            )
                                        </li>
                                    ))}
                            </ul>
                        </div>
                        {conflict.assignments.length > 0 && (
                            <div>
                                <div className="font-medium">
                                    Overlapping assignments
                                </div>
                                <ul className="mt-1 list-disc pl-5">
                                    {conflict.assignments.map((assignment) => (
                                        <li
                                            key={`${assignment.requestedShiftId}:${assignment.conflictingShiftId}`}
                                        >
                                            {assignment.patientName} (
                                            {formatDateAndTimeWithWeekday(
                                                assignment.shiftStartTime,
                                                displayTimezone,
                                            )}
                                            –
                                            {formatTime(
                                                assignment.shiftEndTime,
                                                displayTimezone,
                                            )}
                                            )
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onCancel}
                        disabled={isCalling}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant="destructive"
                        disabled={conflict === null || isCalling}
                        onClick={() => {
                            if (conflict) {
                                onConfirm(
                                    conflict.caregiverId,
                                    conflict.shiftIds,
                                );
                            }
                        }}
                    >
                        {isCalling ? 'Calling…' : 'Call anyway'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
